/**
 * Response-shape normalization for the support data door.
 *
 * This is the projects-app pattern, centralized. Projects normalizes every list
 * response where it enters app state — `tasksSlice.fulfilled`,
 * `ProjectHierarchyView#normalizeTasks`, `FeatureRow`, `useModuleLoader` all do
 * some form of:
 *
 *     const list = Array.isArray(raw) ? raw : (raw.data ?? []);
 *
 * Two things make that worth copying rather than trusting the declared type:
 *
 *   1. `apiService` unwraps ONE envelope layer (`res.data.data`). Anything that
 *      changes the wire format — a payload-encryption toggle that the client
 *      isn't configured for, a gateway that re-wraps, a stale deployed service —
 *      leaves the consumer holding a value that is not the declared type. The
 *      TypeScript type is a compile-time claim; at runtime it is whatever
 *      arrived.
 *   2. Those values reach array methods inside render/`useMemo`, so the throw is
 *      a RENDER throw. React unmounts the subtree — which is how a single bad
 *      response white-screened the whole support remote instead of degrading one
 *      panel.
 *
 * `toList` also RECOVERS the still-enveloped case (`{ success, statusCode, data:
 * [...] }`) by reading one level in, exactly like projects' `raw.data ?? []` —
 * so a transport regression degrades to working UI rather than an empty one
 * wherever the real rows are still reachable.
 */

/** Warn once per context so a polling/refetching query can't flood the console. */
const warned = new Set<string>();

function warnOnce(context: string, detail: string): void {
  const memoKey = `${context}:${detail}`;
  if (warned.has(memoKey)) return;
  warned.add(memoKey);
  console.warn(`[services-support] ${context}: ${detail}`);
}

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

/**
 * Reads one envelope layer back off a response that still carries it.
 *
 * `apiService` already unwraps `res.data.data`. A value that STILL looks like
 * `{ success, statusCode, data }` therefore arrived double-wrapped, and the
 * payload we want is one level in.
 */
function peelEnvelope(value: unknown): unknown {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return value;
  const obj = value as Record<string, unknown>;
  if (!('data' in obj)) return value;
  if (!('success' in obj) && !('statusCode' in obj) && !('message' in obj)) return value;
  return obj.data;
}

/**
 * Coerce an API value that MUST be a list into one — the projects
 * `Array.isArray(raw) ? raw : (raw.data ?? [])` rule.
 *
 * Use as a React Query `select` on every array-returning support query so
 * consumers can call `.map` / `.find` / `.some` / `.filter` without each one
 * re-guarding. Never throws, never returns a non-array.
 *
 * The parameter is typed `T[]` so `select: (d) => toList(d, 'masters')` infers
 * the element type with no generic and no extra import at the call site. That
 * declared type is exactly the compile-time claim this function exists to stop
 * trusting — the body treats the value as `unknown`.
 */
export function toList<T>(raw: T[] | undefined, context: string): T[] {
  if (Array.isArray(raw)) return raw;
  if (raw === undefined || raw === null) return [];

  const inner = peelEnvelope(raw);
  if (Array.isArray(inner)) {
    warnOnce(context, 'response arrived still enveloped; read the list one level in');
    return inner as T[];
  }

  warnOnce(
    context,
    `expected a list, received ${describe(raw)} — falling back to an empty list. The response does not match its contract.`,
  );
  return [];
}

/**
 * The object counterpart: coerce a value that MUST be a record into one, or
 * `undefined` when it cannot be. Callers keep using `?.` on the result, so a
 * malformed response reads as "nothing loaded" instead of throwing.
 */
export function toRecord<T extends object>(raw: unknown, context: string): T | undefined {
  if (raw === undefined || raw === null) return undefined;

  const inner = peelEnvelope(raw);
  if (inner !== raw && inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
    warnOnce(context, 'response arrived still enveloped; read the object one level in');
    return inner as T;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as T;

  warnOnce(
    context,
    `expected an object, received ${describe(raw)} — treating it as absent. The response does not match its contract.`,
  );
  return undefined;
}

/**
 * A list nested inside a response object (e.g. a notification page's
 * `notifications`). Returns `[]` for a missing / non-array field rather than
 * letting `flatMap` splice `undefined` into the flattened result.
 */
export function toNestedList<T>(value: unknown, context: string): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value !== undefined && value !== null) {
    warnOnce(context, `expected a nested list, received ${describe(value)} — using an empty list.`);
  }
  return [];
}

/** Test-only: clear the warn-once memo between cases. */
export function __resetSafeDataWarningsForTests(): void {
  warned.clear();
}
